import type { ComponentProps } from 'react';

export type SvgAssetProps = Omit<ComponentProps<'svg'>, 'name'> & { name: string; src: string };

/**
 * Renders an SVG using the <use> element to reference an external SVG file.
 *
 * This approach is optimized for individual SVG files (one SVG per file),
 * but also supports SVG sprite files when needed.
 *
 * Benefits:
 * - Optimized download size when loading individual SVGs
 * - Browser caching of frequently used SVGs
 * - Ability to style SVGs with CSS
 *
 * @param name - The ID of the SVG symbol within the file
 * @param src - Path to the SVG file, can be absolute or relative
 *
 * @example
 * // Basic usage with individual SVG file
 * <SvgAsset name="illustration" src="/assets/illustration.svg" />
 *
 * // With custom styling
 * <SvgAsset name="diagram" src="/assets/diagram.svg" className="w-full h-auto" />
 *
 * // Using a sprite file (multiple SVGs in one file)
 * <SvgAsset name="chart" src="/assets/sprites.svg" className="w-full max-w-md" />
 *
 * @remarks
 * SVG files must follow this rules:
 * - Each SVG file should contain a single <symbol> tag with the SVG contents
 * - The symbol must have an id attribute matching the `name` prop
 * - The viewBox attribute should be set on the symbol tag, not on the root SVG
 *
 * Example of a proper individual SVG file (illustration.svg):
 * ```svg
 * <svg xmlns="http://www.w3.org/2000/svg">
 *   <symbol id="illustration" viewBox="0 0 800 600">
 *     <path d="..." />
 *   </symbol>
 * </svg>
 * ```
 *
 * Sprite files with multiple SVGs are also supported when needed for specific use cases.
 */
export function SvgAsset({ name, src, ...other }: SvgAssetProps) {
   const href = `${src}#${name}`;
   return (
      <svg data-slot="svg-asset" aria-hidden="true" focusable="false" {...other}>
         <use href={href} />
      </svg>
   );
}
